import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Link } from 'lucide-react';

interface ButtonItem {
  id: string;
  icon: string;
  title: string;
  subtitle?: string;
  link: string;
  linkType: 'internal' | 'external';
  color?: string;
  backgroundColor?: string;
  iconSize?: 'small' | 'medium' | 'large';
}

interface PremiumButtonGridProps {
  title?: string;
  subtitle?: string;
  titleAlign?: 'left' | 'center' | 'right';
  titleSize?: string;
  titleWeight?: string;
  subtitleSize?: string;
  titleGap?: number;
  buttons: ButtonItem[];
  variant?: 'default' | 'rounded' | 'card' | 'gradient' | 'horizontal';
  columns?: number;
  gridSize?: 2 | 4 | 6 | 8;
  gap?: number;
  buttonHeight?: string;
  borderRadius?: string;
  shadowLevel?: 'none' | 'sm' | 'md' | 'lg';
  // New comprehensive controls
  containerPadding?: string;
  titleMarginBottom?: number;
  subtitleMarginBottom?: number;
  buttonSpacing?: number;
  buttonPadding?: string;
  titleColor?: string;
  subtitleColor?: string;
  buttonTextSize?: string;
  buttonSubtitleSize?: string;
  iconSize?: string;
  iconMargin?: number;
  horizontalPadding?: string;
  horizontalIconSize?: number;
  horizontalTextSize?: string;
  horizontalSubtitleSize?: string;
  styles?: Record<string, any>;
}

export const PremiumButtonGrid: React.FC<PremiumButtonGridProps> = ({
  title,
  subtitle,
  titleAlign = 'center',
  titleSize = 'text-3xl',
  titleWeight = 'font-bold',
  subtitleSize = 'text-lg',
  titleGap = 16,
  buttons = [],
  variant = 'default',
  columns = 2,
  gridSize = 4,
  gap = 16,
  buttonHeight = '120px',
  borderRadius = '12px',
  shadowLevel = 'md',
  // New comprehensive controls defaults
  containerPadding = '24px',
  titleMarginBottom = 8,
  subtitleMarginBottom = 24,
  buttonSpacing,
  buttonPadding = '16px',
  titleColor,
  subtitleColor,
  buttonTextSize = 'text-sm',
  buttonSubtitleSize = 'text-xs',
  iconSize = 'text-4xl',
  iconMargin = 8,
  horizontalPadding = '12px',
  horizontalIconSize = 24,
  horizontalTextSize = 'text-sm',
  horizontalSubtitleSize = 'text-xs',
  styles = {}
}) => {
  const getShadowClass = () => {
    switch (shadowLevel) {
      case 'none': return '';
      case 'sm': return 'shadow-sm';
      case 'md': return 'shadow-md';
      case 'lg': return 'shadow-lg';
      default: return 'shadow-md';
    }
  };

  const getIconSize = (size?: string) => {
    switch (size) {
      case 'small': return 'text-2xl';
      case 'large': return 'text-5xl';
      default: return 'text-4xl';
    }
  };

  const getGridColumns = () => {
    const displayCount = Math.min(buttons.length, gridSize);

    // For horizontal variant, always use 2 columns
    if (variant === 'horizontal') {
      return 'grid-cols-2';
    }

    // For other variants
    if (displayCount <= 2) return 'grid-cols-2';
    if (displayCount === 3) return 'grid-cols-3';
    if (displayCount === 4) return 'grid-cols-2';
    if (displayCount <= 6) return 'grid-cols-2'; // 6 buttons = 3 rows x 2 columns
    if (displayCount === 8) return 'grid-cols-2 md:grid-cols-4'; // 8 buttons = 4x2 on mobile, 2x4 on desktop
    return 'grid-cols-2';
  };

  const renderButton = (button: ButtonItem, index: number) => {
    const isEmoji = !button.icon.startsWith('http') && !button.icon.startsWith('/');

    // Horizontal variant with icon background
    if (variant === 'horizontal') {
      const iconColors = [
        { bg: '#E3F2FD', color: '#1976D2' }, // Blue
        { bg: '#E8F5E9', color: '#388E3C' }, // Green
        { bg: '#FFF9C4', color: '#F57C00' }, // Yellow
        { bg: '#FFEBEE', color: '#D32F2F' }, // Red
        { bg: '#F3E5F5', color: '#7B1FA2' }, // Purple
        { bg: '#FFF3E0', color: '#F57C00' }, // Orange
        { bg: '#E0F2F1', color: '#00796B' }, // Teal
        { bg: '#EFEBE9', color: '#5D4037' }, // Brown
      ];

      const colorScheme = iconColors[index % iconColors.length];

      return (
        <motion.div
          key={button.id}
          className="relative flex items-center bg-white hover:bg-gray-50 transition-all cursor-pointer shadow-md hover:shadow-lg"
          style={{
            borderRadius: borderRadius,
            padding: horizontalPadding,
            height: '70px',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => console.log('Button clicked:', button.title, button.link)}
        >
          {/* Icon with colored background */}
          <div
            className="flex items-center justify-center"
            style={{
              width: `${horizontalIconSize + 22}px`,
              height: `${horizontalIconSize + 22}px`,
              backgroundColor: button.backgroundColor || colorScheme.bg,
              borderRadius: '10px',
              flexShrink: 0,
              marginRight: `${iconMargin}px`,
            }}
          >
            {isEmoji ? (
              <span style={{ fontSize: `${horizontalIconSize}px` }}>{button.icon}</span>
            ) : (
              <img
                src={button.icon}
                alt={button.title}
                style={{ width: `${horizontalIconSize}px`, height: `${horizontalIconSize}px` }}
                className="object-contain"
              />
            )}
          </div>

          {/* Text content */}
          <div className="flex-1 text-left">
            <div className={`font-semibold text-gray-800 ${horizontalTextSize} leading-tight`}>
              {button.title}
            </div>
            {button.subtitle && (
              <div className={`${horizontalSubtitleSize} text-gray-500 mt-0.5`}>
                {button.subtitle}
              </div>
            )}
          </div>

          {/* External link indicator */}
          {button.linkType === 'external' && (
            <ExternalLink className="w-3 h-3 text-gray-400 ml-2" />
          )}
        </motion.div>
      );
    }

    const buttonContent = (
      <>
        {/* Icon */}
        <div className={`${iconSize}`} style={{ marginBottom: `${iconMargin}px` }}>
          {isEmoji ? (
            <span>{button.icon}</span>
          ) : (
            <img
              src={button.icon}
              alt={button.title}
              className="w-12 h-12 mx-auto object-contain"
            />
          )}
        </div>

        {/* Title */}
        <div className={`font-medium text-gray-800 ${buttonTextSize}`}>
          {button.title}
        </div>

        {/* Subtitle */}
        {button.subtitle && (
          <div className={`${buttonSubtitleSize} text-gray-500 mt-1`}>
            {button.subtitle}
          </div>
        )}

        {/* Link indicator */}
        {button.linkType === 'external' && (
          <div className="absolute top-2 right-2 text-gray-400">
            <ExternalLink className="w-4 h-4" />
          </div>
        )}
      </>
    );

    const baseStyle = {
      height: buttonHeight,
      borderRadius: borderRadius,
      backgroundColor: button.backgroundColor || '#ffffff',
      borderColor: button.color || '#e5e7eb',
    };

    switch (variant) {
      case 'rounded':
        return (
          <motion.div
            key={button.id}
            className={`relative flex flex-col items-center justify-center border-2 hover:scale-105 transition-transform cursor-pointer ${getShadowClass()}`}
            style={{
              ...baseStyle,
              borderRadius: '24px',
              padding: buttonPadding,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => console.log('Button clicked:', button.title, button.link)}
          >
            {buttonContent}
          </motion.div>
        );

      case 'card':
        return (
          <motion.div
            key={button.id}
            className={`relative flex flex-col items-center justify-center p-4 bg-white hover:shadow-xl transition-all cursor-pointer ${getShadowClass()}`}
            style={{
              ...baseStyle,
              borderRadius: '16px',
              border: '1px solid #e5e7eb',
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => console.log('Button clicked:', button.title, button.link)}
          >
            {buttonContent}
          </motion.div>
        );

      case 'gradient':
        const gradients = [
          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
          'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        ];

        return (
          <motion.div
            key={button.id}
            className={`relative flex flex-col items-center justify-center p-4 text-white hover:scale-105 transition-transform cursor-pointer ${getShadowClass()}`}
            style={{
              ...baseStyle,
              background: button.backgroundColor || gradients[index % gradients.length],
            }}
            initial={{ opacity: 0, rotate: -5 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.05, rotate: 2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => console.log('Button clicked:', button.title, button.link)}
          >
            <div className={`mb-2 ${getIconSize(button.iconSize)}`}>
              {isEmoji ? (
                <span className="filter drop-shadow-lg">{button.icon}</span>
              ) : (
                <img
                  src={button.icon}
                  alt={button.title}
                  className="w-12 h-12 mx-auto object-contain filter drop-shadow-lg"
                />
              )}
            </div>
            <div className="font-semibold">{button.title}</div>
            {button.subtitle && (
              <div className="text-sm opacity-90 mt-1">{button.subtitle}</div>
            )}
            {button.linkType === 'external' && (
              <div className="absolute top-2 right-2 text-white/70">
                <ExternalLink className="w-4 h-4" />
              </div>
            )}
          </motion.div>
        );

      default: // 'default' variant
        return (
          <motion.div
            key={button.id}
            className={`relative flex flex-col items-center justify-center p-4 bg-white border hover:border-blue-500 transition-colors cursor-pointer ${getShadowClass()}`}
            style={baseStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => console.log('Button clicked:', button.title, button.link)}
          >
            {buttonContent}
          </motion.div>
        );
    }
  };

  const actualGap = buttonSpacing || gap;

  return (
    <div
      style={{
        padding: containerPadding,
        backgroundColor: styles.backgroundColor,
        ...styles
      }}
    >
      {/* Title Section */}
      {(title || subtitle) && (
        <div style={{ textAlign: titleAlign, marginBottom: `${subtitleMarginBottom}px` }}>
          {title && (
            <h2
              className={`${titleSize} ${titleWeight}`}
              style={{
                color: titleColor || '#1f2937',
                marginBottom: `${titleMarginBottom}px`
              }}
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <p
              className={`${subtitleSize}`}
              style={{ color: subtitleColor || '#6b7280' }}
            >
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Button Grid */}
      <motion.div
        className={`grid ${getGridColumns()}`}
        style={{ gap: `${actualGap}px` }}
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.05
            }
          }
        }}
      >
        {buttons.slice(0, gridSize).map((button, index) =>
          renderButton(button, index)
        )}
      </motion.div>
    </div>
  );
};

export default PremiumButtonGrid;